import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  UserOutlined, PhoneOutlined, MailOutlined, HomeOutlined,
  BankOutlined, LinkOutlined, MessageOutlined, TagsOutlined,
  FundProjectionScreenOutlined, ArrowLeftOutlined, SaveOutlined,
  PlusOutlined, EditOutlined, GlobalOutlined,
  CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined,
} from "@ant-design/icons";
import { useEnquiryDetails } from "../hooks/useManageEnquiry";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { parsePhones, COUNTRY_CODES, isValidPhone } from "../utils/phones";
import { useQueryClient } from "@tanstack/react-query";



 /* ── Reusable Section Card ── */
  const SectionCard = ({ icon, title, subtitle, children, delay = "0s" }) => (
    <div style={{ background: "white", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,.06)", border: "1px solid #f0f0f0", marginBottom: "24px", overflow: "hidden", animation: `fadeInUp 0.3s ease ${delay} both` }}>
      <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "20px 28px", borderLeft: "4px solid #667eea", background: "linear-gradient(135deg,#f8f9ff,#f0f4ff)" }}>
        <div style={{ width: "40px", height: "40px", background: "linear-gradient(135deg,#667eea,#764ba2)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "17px", flexShrink: 0 }}>{icon}</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: "16px", color: "#1f2937" }}>{title}</div>
          <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "1px" }}>{subtitle}</div>
        </div>
      </div>
      <div style={{ padding: "24px 28px" }}>{children}</div>
    </div>
  );


  const fieldBase = { width: "100%", padding: "10px 14px", border: "1.5px solid #e5e7eb", borderRadius: "10px", fontSize: "14px", color: "#1f2937", background: "white", outline: "none", transition: "border-color 0.2s, box-shadow 0.2s", fontFamily: "inherit", boxSizing: "border-box" };
  const fieldErr  = { ...fieldBase, borderColor: "#fca5a5", background: "#fff8f8" };
  const onFocus   = (e) => { e.target.style.borderColor = "#667eea"; e.target.style.boxShadow = "0 0 0 3px rgba(102,126,234,.12)"; };
  const onBlur    = (hasErr) => (e) => { e.target.style.borderColor = hasErr ? "#fca5a5" : "#e5e7eb"; e.target.style.boxShadow = "none"; };
  const labelSt   = { fontSize: "12px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" };

   const Field = ({ label, icon, name, type = "text", placeholder, required, isTextarea, value, onChange, error }) => (
  <div>
    <div style={labelSt}>{icon} {label}{required && <span style={{ color: "#ef4444" }}>*</span>}</div>
    {isTextarea ? (
      <textarea name={name} value={value} onChange={onChange} placeholder={placeholder} rows={3}
        style={{ ...(error ? fieldErr : fieldBase), resize: "vertical", minHeight: "88px" }}
        onFocus={onFocus} onBlur={onBlur(!!error)} />
    ) : (
      <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
        style={error ? fieldErr : fieldBase}
        onFocus={onFocus} onBlur={onBlur(!!error)} />
    )}
    {error && <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "5px" }}>⚠ {error}</div>}
  </div>
);



const ViewEnquiry = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [data, setData] = useState({
    enq_name: "", phone: "", country_code: "+91", additional_phone: [], address: "", company: "",
    email: "", source_url: "", message: "",
    enquiry_status: 0, utm_campaign: "", utm_source: "", utm_medium: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: enquiryData, isLoading: isFetching } = useEnquiryDetails(id);

  useEffect(() => {
    if (enquiryData?.data) {
      const d = enquiryData.data;
      setData({
        enq_name:         d.name             || "",
        phone:            d.phone            || "",
        country_code:     d.country_code     || "+91",
        additional_phone: parsePhones(d.additional_phone),
        address:          d.address          || "",
        company:          d.company          || "",
        email:            d.email            || "",
        source_url:       d.source_url       || "",
        message:          d.message          || "",
        enquiry_status:   Number(d.status)   || 0,
        utm_campaign:     d.utm_campaign     || "",
        utm_source:       d.utm_source       || "",
        utm_medium:       d.utm_medium       || "",
      });
    }
  }, [enquiryData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData(p => ({ ...p, [name]: value }));
    if (errors[name]) setErrors(p => ({ ...p, [name]: "" }));
  };

  const validateEmail = (email) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

  // Leads come from anywhere now: E.164 allows 4–15 digits, the country code is
  // a separate field. A hardcoded 10-digit rule would reject every foreign lead.
  const validatePhone = (phone) => isValidPhone(phone);

  const validateForm = () => {
    const errs = {};
    if (!data.enq_name.trim()) errs.enq_name = "Name is required";
    if (!data.phone.trim())    errs.phone    = "Phone is required";
    else if (!validatePhone(data.phone)) errs.phone = "Phone must be 4–15 digits (no country code here)";
    if (!String(data.country_code || "").trim()) errs.country_code = "Country code is required";
    const altPhones = parsePhones(data.additional_phone);
    if (altPhones.some((p) => !validatePhone(p)))
      errs.additional_phone = "Every additional phone must be 4–15 digits";
    else if (altPhones.includes(data.phone.trim()))
      errs.additional_phone = "An additional phone cannot be the same as the primary phone";
    if (!data.email.trim()) errs.email = "Email is required";
    else if (!validateEmail(data.email)) errs.email = "Enter a valid email address";
    return errs;
  };

  const buildPayload = () => ({
    name: data.enq_name, phone: data.phone,
    /* Whoever types a lead in owns it — the API assigns it in the same call, so
       the lead never passes through the shared pool where it could be picked up
       by someone else. Omitting this would leave it to the auto-assign cron. */
    ...(isEdit ? {} : { assign_to: Number(localStorage.getItem("trade_user_id")) || undefined }),
    // Mandatory on the API — the number alone is ambiguous for a global lead.
    country_code: data.country_code || "+91",
    additional_phone: parsePhones(data.additional_phone),
    address: data.address, company: data.company, email: data.email,
    source_url: data.source_url, message: data.message, status: Number(data.enquiry_status),
    utm_campaign: data.utm_campaign, utm_source: data.utm_source, utm_medium: data.utm_medium,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      if (validationErrors.enq_name)        toast.error(validationErrors.enq_name);
      else if (validationErrors.phone)       toast.error(validationErrors.phone);
      else if (validationErrors.additional_phone) toast.error(validationErrors.additional_phone);
      else if (validationErrors.email)       toast.error(validationErrors.email);
      return;
    }
    setIsSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("cms_token")}` };
      const payload = buildPayload();
      if (isEdit) {
        const res = await axios.post(`${API_BASE_URL}/trademark-enquiries-update/${id}`, payload, { headers });
        if (res.data.status) { toast.success("Lead updated successfully!"); navigate("/cms/manageenquiry"); }
        else toast.error(res.data.message || "Something went wrong");
      } else {
        const res = await axios.post(`${API_BASE_URL}/cms-add-trademark-enquiry`, payload, { headers });
        // The API answers 200 in the body on some builds and `true` on others —
        // treat both as success, otherwise the redirect silently never fires.
        const ok = res.data?.status === 200 || res.data?.status === true || res.data?.success;
        if (ok) {
          toast.success(res.data?.message || "Lead added successfully!");


          /* assigned:false is NOT a failure — the lead was created, it just could
             not be given to this user (lead limit). Say so softly and send them to
             the list where the lead actually is. */
          const assigned = res.data?.assigned !== false;
          if (!assigned) {
            toast.info(
              res.data?.reason
                ? `Lead saved, but not assigned to you — ${res.data.reason}.`
                : "Lead saved, but it stayed unassigned.",
            );
          }

          queryClient.invalidateQueries({ queryKey: ["unassigned-leads"], refetchType: "all" });
          queryClient.invalidateQueries({ queryKey: ["enquiryList"], refetchType: "all" });
          queryClient.invalidateQueries({ queryKey: ["inbox-leads"], refetchType: "all" });
          // Send the user where the lead actually is.
          navigate(assigned ? "/leads/assigned-leads" : "/leads/unassigned-leads");
        } else {
          toast.error(res.data?.message || "Something went wrong");
        }
      }
    } catch (err) {
      // Show the API's own reason, never axios's "Request failed with status..."
      const data = err?.response?.data;
      toast.error(
        data?.errors?.[Object.keys(data?.errors || {})[0]]?.[0] ||
          data?.message ||
          data?.error ||
          "Something went wrong",
      );
    }
    finally { setIsSubmitting(false); }
  };

  const statusOptions = [
    { val: 0,  label: "Neutral", desc: "Awaiting follow-up",  color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe", icon: <MinusCircleOutlined /> },
    { val: 1,  label: "Hot Lead",  desc: "High-priority lead",  color: "#e11d48", bg: "#fff1f2", border: "#fecdd3", icon: <CheckCircleOutlined /> },
    { val: 10, label: "Negative",  desc: "Not interested",      color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: <CloseCircleOutlined /> },
  ];


  if (isFetching) return (
    <div className="main-content"><div className="page-content"><div className="container-fluid">
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: "48px", height: "48px", border: "4px solid #e8ecff", borderTopColor: "#667eea", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
          <div style={{ color: "#6b7280", fontSize: "15px", fontWeight: 600 }}>Loading lead details...</div>
        </div>
      </div>
    </div></div></div>
  );

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        .ve-submit { transition: all 0.2s; }
        .ve-submit:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(102,126,234,.45) !important; }
        .ve-back { transition: all 0.2s; }
        .ve-back:hover { background: #f3f4f6 !important; }
        .ve-status-label:hover { border-color: #c7d2fe !important; }
      `}</style>

      <div className="main-content"><div className="page-content"><div className="container-fluid">

        {/* ── PAGE HEADER ── */}
        <div style={{ background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", borderRadius: "16px", padding: "28px 32px", marginBottom: "28px", boxShadow: "0 8px 32px rgba(102,126,234,.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {/* Back to wherever the user came from — this screen is opened from
                several lists, so history beats a hardcoded route. */}
            <button
              type="button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/cms/manageenquiry"))}
              style={{ display: "inline-flex", alignItems: "center", gap: "7px", background: "rgba(255,255,255,.22)", border: "1px solid rgba(255,255,255,.35)", color: "white", borderRadius: "10px", padding: "9px 16px", fontSize: "14px", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              <ArrowLeftOutlined /> Back
            </button>
            <div style={{ width: "56px", height: "56px", background: "rgba(255,255,255,.22)", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px", color: "white" }}>
              {isEdit ? <EditOutlined /> : <PlusOutlined />}
            </div>
            <div>
              <h4 style={{ color: "white", margin: 0, fontSize: "24px", fontWeight: 700 }}>{isEdit ? "Edit Lead" : "Add New Lead"}</h4>
              <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: "14px" }}>{isEdit ? "Update the lead information below" : "Fill in the details to create a new lead"}</p>
            </div>
          </div>
          <div style={{ background: "rgba(255,255,255,.18)", padding: "10px 18px", borderRadius: "10px", display: "flex", gap: "6px", alignItems: "center", fontSize: "13px", color: "white" }}>
            <Link to="/cms/manageenquiry" style={{ color: "white", textDecoration: "none" }}>Manage Lead</Link>
            <span style={{ color: "rgba(255,255,255,.5)" }}>›</span>
            <span style={{ fontWeight: 600 }}>{isEdit ? "Edit Lead" : "Add Lead"}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ── SECTION 1: Personal Information ── */}
          <SectionCard icon={<UserOutlined />} title="Personal Information" subtitle="Basic contact details of the lead" delay="0s">
            <div className="row g-3">
              <div className="col-md-6">
                <Field label="Full Name" icon={<UserOutlined style={{ color: "#667eea" }} />} name="enq_name" placeholder="Enter full name" required
  value={data.enq_name} onChange={handleChange} error={errors.enq_name} />
              </div>
              <div className="col-md-6">
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{ width: 150, flexShrink: 0 }}>
                    <div style={{ fontWeight: 600, color: "#374151", fontSize: 13, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                      <PhoneOutlined style={{ color: "#667eea" }} /> Code
                      <span style={{ color: "#ef4444" }}>*</span>
                    </div>
                    <select
                      name="country_code"
                      value={data.country_code}
                      onChange={handleChange}
                      style={{ width: "100%", padding: "11px 12px", border: "2px solid #e8ecff", borderRadius: 10, fontSize: 14, background: "white", outline: "none", cursor: "pointer" }}
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.code} · {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                <Field label="Phone" icon={<PhoneOutlined style={{ color: "#667eea" }} />} name="phone" placeholder="Number without country code" required
  value={data.phone} onChange={handleChange} error={errors.phone} />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                {/* A lead can carry any number of alternates — one input each,
                    added on demand, so the form never caps what the API allows. */}
                <label style={{ fontWeight: 600, color: "#374151", fontSize: 13, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                  <PhoneOutlined style={{ color: "#667eea" }} /> Additional Phone
                </label>
                {(data.additional_phone || []).map((val, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                      className={`form-control ${errors.additional_phone ? "is-invalid" : ""}`}
                      placeholder="Enter alternate phone number"
                      value={val}
                      maxLength={10}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setData((prev) => ({
                          ...prev,
                          additional_phone: prev.additional_phone.map((p, i) => (i === idx ? v : p)),
                        }));
                        if (errors.additional_phone) setErrors((p) => ({ ...p, additional_phone: "" }));
                      }}
                    />
                    <button
                      type="button"
                      title="Remove this number"
                      onClick={() => setData((prev) => ({ ...prev, additional_phone: prev.additional_phone.filter((_, i) => i !== idx) }))}
                      style={{ width: 38, flexShrink: 0, borderRadius: 8, border: "1.5px solid #fecaca", background: "#fef2f2", color: "#dc2626", fontSize: 16, lineHeight: 1, cursor: "pointer" }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setData((prev) => ({ ...prev, additional_phone: [...(prev.additional_phone || []), ""] }))}
                  disabled={(data.additional_phone || []).some((p) => !p)}
                  style={{ width: "100%", padding: 8, border: "1.5px dashed #c7d2fe", borderRadius: 8, background: "#f8f9ff", color: "#667eea", fontSize: 12, fontWeight: 600, cursor: (data.additional_phone || []).some((p) => !p) ? "not-allowed" : "pointer", opacity: (data.additional_phone || []).some((p) => !p) ? 0.6 : 1 }}
                >
                  + Add {(data.additional_phone || []).length ? "another" : "an"} alternate number
                </button>
                {errors.additional_phone && (
                  <div style={{ color: "#dc2626", fontSize: 12, marginTop: 5 }}>{errors.additional_phone}</div>
                )}
              </div>
              <div className="col-md-6">
                <Field label="Email" icon={<MailOutlined style={{ color: "#667eea" }} />} name="email" type="email" placeholder="Enter email address" required
  value={data.email} onChange={handleChange} error={errors.email} />
              </div>
              <div className="col-md-6">
                <Field label="Company" icon={<BankOutlined style={{ color: "#667eea" }} />} name="company" placeholder="Enter company name"
  value={data.company} onChange={handleChange} error={errors.company} />
              </div>
              <div className="col-12">
                <Field label="Address" icon={<HomeOutlined style={{ color: "#667eea" }} />} name="address" placeholder="Enter full address" isTextarea
  value={data.address} onChange={handleChange} error={errors.address} />
              </div>
            </div>
          </SectionCard>

          {/* ── SECTION 2: Enquiry Details ── */}
          <SectionCard icon={<MessageOutlined />} title="Enquiry Details" subtitle="Source URL and message from the lead" delay="0.06s">
            <div className="row g-3">
              <div className="col-12">
                <Field label="Source URL" icon={<LinkOutlined style={{ color: "#667eea" }} />} name="source_url" placeholder="https://example.com/page" 
                 value={data.source_url} onChange={handleChange} error={errors.source_url}
                />
              </div>
              <div className="col-12">
                <Field label="Message" icon={<MessageOutlined style={{ color: "#667eea" }} />} name="message" placeholder="Enter enquiry message..." isTextarea 
                  value={data.message} onChange={handleChange} error={errors.message}
                />
              </div>
            </div>
          </SectionCard>

          {/* ── SECTION 3: Lead Status ── */}
          <SectionCard icon={<TagsOutlined />} title="Lead Status" subtitle="Set the current status of this lead" delay="0.12s">
            <div className="row g-3">
              {statusOptions.map(s => (
                <div className="col-md-4" key={s.val}>
                  <label className="ve-status-label"
                    style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 18px", borderRadius: "12px", border: `2px solid ${data.enquiry_status == s.val ? s.border : "#e5e7eb"}`, background: data.enquiry_status == s.val ? s.bg : "white", cursor: "pointer", transition: "all 0.2s", userSelect: "none" }}>
                    <input type="radio" name="enquiry_status" value={s.val} checked={data.enquiry_status == s.val} onChange={handleChange}
                      style={{ accentColor: s.color, width: "16px", height: "16px", flexShrink: 0 }} />
                    <div style={{ color: s.color, fontSize: "20px", flexShrink: 0 }}>{s.icon}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: "14px", color: data.enquiry_status == s.val ? s.color : "#374151" }}>{s.label}</div>
                      <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "2px" }}>{s.desc}</div>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* ── SECTION 4: UTM Parameters ── */}
          <SectionCard icon={<FundProjectionScreenOutlined />} title="UTM Parameters" subtitle="Marketing attribution and campaign tracking data" delay="0.18s">
            <div className="row g-3">
              <div className="col-md-4">
                <Field label="UTM Source" icon={<GlobalOutlined style={{ color: "#667eea" }} />} name="utm_source" placeholder="e.g. google" 
                  value={data.utm_source} onChange={handleChange} error={errors.utm_source}
                />
              </div>
              <div className="col-md-4">
                <Field label="UTM Medium" icon={<FundProjectionScreenOutlined style={{ color: "#667eea" }} />} name="utm_medium" placeholder="e.g. cpc" 
                  value={data.utm_medium} onChange={handleChange} error={errors.utm_medium}
                />
              </div>
              <div className="col-md-4">
                <Field label="UTM Campaign" icon={<TagsOutlined style={{ color: "#667eea" }} />} name="utm_campaign" placeholder="e.g. spring_sale" 
                   value={data.utm_campaign} onChange={handleChange} error={errors.utm_campaign}
                />
              </div>
            </div>
          </SectionCard>

          {/* ── ACTION BUTTONS ── */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px", paddingBottom: "32px" }}>
            <Link to="/cms/manageenquiry" className="ve-back"
              style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "11px 24px", background: "white", color: "#6b7280", borderRadius: "10px", border: "2px solid #e5e7eb", fontWeight: 600, fontSize: "14px", textDecoration: "none" }}>
              <ArrowLeftOutlined /> Cancel
            </Link>
            <button type="submit" disabled={isSubmitting} className="ve-submit"
              style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "11px 32px", background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", color: "white", borderRadius: "10px", border: "none", cursor: isSubmitting ? "not-allowed" : "pointer", fontWeight: 700, fontSize: "15px", boxShadow: "0 4px 14px rgba(102,126,234,.35)", opacity: isSubmitting ? 0.8 : 1 }}>
              {isSubmitting
                ? <><span className="spinner-border spinner-border-sm" />&nbsp;{isEdit ? "Updating..." : "Adding..."}</>
                : isEdit ? <><SaveOutlined /> Update Lead</> : <><PlusOutlined /> Add Lead</>
              }
            </button>
          </div>

        </form>

      </div></div></div>
    </>
  );
};

export default ViewEnquiry;
